import { Grid, MenuItem, Select, Box, Button } from '@mui/material';
import React, { useEffect, useState } from 'react';
import Patients from './Patients';
import {
	getAdmittedPatients,
	getLocation,
	getPracticeStaff,
} from '../../../services/api/apiManager';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const missedFinderPaginationSx = {
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-end',
	padding: '12px 18px',
	gap: '16px',
	borderTop: '1px solid #e3edf8',
	background: '#fff',
	fontFamily: 'Poppins',
	fontSize: '13px',
	color: '#0b2450',
	fontWeight: 600,
};

const missedFinderPageButtonSx = {
	minWidth: 36,
	width: 36,
	height: 36,
	border: '1px solid #dbe7f5',
	borderRadius: '50%',
	color: '#0f5bca',
	background: '#fff',
	boxShadow: 'none',
	'&:hover': {
		background: '#f3f7ff',
		boxShadow: 'none',
	},
	'&.Mui-disabled': {
		color: '#b9c8dd',
		background: '#fff',
		borderColor: '#e5edf7',
	},
};

function MissedChargeFinder() {
	const practice_id = localStorage.getItem('practice_id');
	const [provider, setProvider] = useState('');
	const [location, setLocation] = useState('');
	const [staffList, setStaffList] = useState([]);
	const [locations, setLocations] = useState([]);
	const [visitData, setVisitData] = useState([]);
	const [searchText, setSearchText] = useState('');
	const [loading, setLoading] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [filteredVisits, setFilteredVisits] = useState(visitData);

	const totalPages = Math.ceil(filteredVisits?.length / rowsPerPage);
	const startRecord =
		filteredVisits?.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
	const endRecord = Math.min(currentPage * rowsPerPage, filteredVisits?.length);

	const handleChangePage = (newPage) => {
		if (newPage > 0) {
			setCurrentPage(newPage);
		}
	};

	const handleChangeRowsPerPage = (e) => {
		setRowsPerPage(parseInt(e.target.value));
		setCurrentPage(1);
	};

	const getDataForAdmittedPatients = async () => {
		try {
			setLoading(true);
			const response = await getAdmittedPatients(practice_id);
			if (response?.status === 200) {
				setVisitData(response?.data);
			} else {
				setVisitData([]);
			}
			setLoading(false);
		} catch (error) {
			console.error('Error fetching data:', error);
			setLoading(false);
		}
	};

	useEffect(() => {
		getDataForAdmittedPatients();
	}, []);

	useEffect(() => {
		if (
			visitData &&
			searchText?.length === 0 &&
			provider?.length === 0 &&
			location?.length === 0
		) {
			setFilteredVisits(visitData);
		}
	}, [visitData]);

	const handleProvider = (event) => {
		const selectedProvider = event.target.value;
		setProvider(selectedProvider);

		const filterVisits = selectedProvider
			? visitData?.filter((visit) => visit?.providerid == selectedProvider)
			: visitData;

		setFilteredVisits(filterVisits);
		setCurrentPage(1);
	};

	const handleLocation = (event) => {
		const selectedLocation = event.target.value;
		setLocation(selectedLocation);
		const filterVisits = selectedLocation
			? visitData?.filter((visit) => visit?.locationid == selectedLocation)
			: visitData;

		setFilteredVisits(filterVisits);
		setCurrentPage(1);
	};

	const fetchPracticeStaff = async () => {
		const res = await getPracticeStaff();
		if (res && res.data && res.data.length > 0) {
			setStaffList(res.data);
		} else {
			setStaffList([]);
		}
	};

	const getLocationsData = async () => {
		const resp = await getLocation();
		if (resp?.status === 200) {
			setLocations(resp?.data);
		}
	};

	useEffect(() => {
		fetchPracticeStaff();
		getLocationsData();
	}, []);

	return (
		<Grid
			container
			sx={{
				background: '#f5f8fd',
				padding: '12px',
				fontFamily: 'Poppins',
			}}
		>
			<Grid
				item
				xs={12}
				sm={12}
				md={12}
				lg={12}
				xl={12}
				sx={{
					border: '1px solid #dce8f5',
					background: 'white',
					borderRadius: '8px',
					overflow: 'hidden',
				}}
			>
				<Patients
					visitData={visitData}
					filteredVisits={filteredVisits}
					setFilteredVisits={setFilteredVisits}
					setVisitData={setVisitData}
					loading={loading}
					setLoading={setLoading}
					getDataForAdmittedPatients={getDataForAdmittedPatients}
					searchText={searchText}
					setSearchText={setSearchText}
					provider={provider}
					location={location}
					handleProvider={handleProvider}
					handleLocation={handleLocation}
					locations={locations}
					staffList={staffList}
					currentPage={currentPage}
					rowsPerPage={rowsPerPage}
				/>
				<Box sx={missedFinderPaginationSx}>
					<span>Rows per page:</span>
					<Select
						value={rowsPerPage}
						onChange={(e) => handleChangeRowsPerPage(e)}
						displayEmpty
						size="small"
						sx={{
							height: 36,
							minWidth: 68,
							borderRadius: '6px',
							fontFamily: 'Poppins',
							fontSize: '13px',
							'& .MuiOutlinedInput-notchedOutline': {
								borderColor: '#dbe7f5',
							},
							'& .MuiSelect-select': {
								padding: '7px 28px 7px 12px',
							},
						}}
					>
						{[5, 10, 50, 100].map((option) => (
							<MenuItem key={option} value={option}>
								{option}
							</MenuItem>
						))}
					</Select>
					<span>{`${startRecord}-${endRecord} of ${filteredVisits?.length}`}</span>
					<Button
						onClick={() => handleChangePage(currentPage - 1)}
						disabled={currentPage === 1}
						sx={missedFinderPageButtonSx}
					>
						<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
					</Button>
					<Button
						onClick={() => handleChangePage(currentPage + 1)}
						disabled={currentPage === totalPages || totalPages === 0}
						sx={missedFinderPageButtonSx}
					>
						<ArrowForwardIosIcon sx={{ fontSize: 14 }} />
					</Button>
				</Box>
			</Grid>
		</Grid>
	);
}

export default MissedChargeFinder;
